// equip-rules.ts — Janus lane I2/I3 S2b (2026-09-28, owner: "this should all be frontend"): the equip family's RULES in the browser —
// the twins of the PHP the client-truth equip envelope no longer runs:
//   validate_equip_conditions()              (move_inventory_inc.php:2017 — the free-hand equip: hard fails, size, two-weapon fighting)
//   validate_equip_size_match() + heavy      (the SWAP runs only these, count-independent — equip_or_swap_inventory_inc.php)
//   check_and_update_caster_aps_for_move_inventory()  (the AP table: 1, a shield 2, a start-don / start-doff 3, a Fast Draw weapon 0)
//   swap_hands 1 AP in combat
// Pure: every read goes through the injected readers (the page binds them to its caches — inventory-equip-truth.ts). A fact the page
// cannot establish (a feat / passive definition it does not hold, an unidentified item's soft downgrade) answers `unknown`, and the caller
// hands the action back to the legacy endpoint.
export type Row = Record<string, unknown>;
export type RuleVerdict = { ok: true } | { ok: false; error: string } | { unknown: string };

/** PHP catalog constants (tools/probes/i23_equip_rule_constants.php dumps the live ones — keep in step). */
export const EQUIP_RULE_CONSTANTS = {
  /** SizeType::getOversizedFitPassives(): a non-standard size needs its matching fit passive on the item. */
  oversized: {
    '13456FEC-A2EE-DB46-5829-0EB2F627E1B7': { weapon: 'C80A28EC-E4DF-664C-FA7C-870A8A53C032', armor: '64C7938A-010C-1527-6890-A4BC39362807' },
    '52FFDB16-A991-C8D4-FE59-7C75EE6CF7AD': { weapon: 'BD02A97E-8EB3-445A-8758-B01E089FFE03', armor: '83D21DE1-B6D9-877E-9B6E-56734C400643' },
    'D0EF6D7A-82BC-1E6E-9D36-5004C7E0561C': { weapon: 'F74CFA32-D114-191D-CC2F-C8DE12B8E871', armor: 'BE936924-6C8B-CBEC-3E77-6D47B1F98959' },
    'CEA2946E-D395-46EA-A6E7-74D21EA2376E': { weapon: '4C7C35F9-F948-581F-0F2D-11BAFC476122', armor: '65532C69-CC39-2614-CABC-C4D5EB9ACE48' },
    'C94CDD74-8ACD-26B0-5C53-4CA29CF21D68': { weapon: 'C05381EC-D66A-15AC-46F0-42F5EA8D9383', armor: '7C0A9429-D170-041B-727B-5D3762D9CFC5' },
    '4C33FD22-5382-15DA-1C46-F57F14378AFD': { weapon: '44D45637-FCFE-FC95-BE32-DD47FCAEC116', armor: 'A0DC40FE-BA7A-89FC-CA10-1C15BBAD6EDF' },
  } as Record<string, { weapon: string; armor: string }>,
  /** Small, Medium: an item must NOT carry any oversized fit passive. */
  standard: ['AAC1F466-7A8B-C593-1ADC-FC402B8909D7', 'DC23AA88-CB3D-EA4E-F6F7-6389ABC4F9DC'],
  /** WeaponClassificationType::getColossalEquipRequirements(): the trained passive or the Master feat. */
  colossal: {
    'B66B3311-4A62-5531-D446-CE4A565FD181': { passive: '5E7E3B4E-3350-448C-E959-0C7A59D4867E', feat: '9A5C417C-D482-199D-4FDB-2296A7DF6F7F' },
    'B12431F6-BB7A-8079-002F-111914B9E65A': { passive: '867FE4B0-29AC-B2D9-B91B-CC468789C17D', feat: 'F3298F3D-8106-B017-D009-B66B4F7D7785' },
  } as Record<string, { passive: string; feat: string }>,
  shield: '376E7991-D1E3-D280-5186-F09AE6F90CD8',
  /** WeaponGripFeat::LIST — the feat, or the passive it grants. */
  grip: {
    secrets_of_shirion: { feat: 'D1C7CFFE-0188-450B-8FEA-39BAE0781308', passive: '107CF709-348E-4234-B92C-D7BA6F5579CB' },
    titans_grip: { feat: '03A3A22E-6789-4418-9088-7171A0E083C3', passive: 'F277610B-299C-49B4-9773-2F1A3F3E80FB' },
  },
  vehicles: ['vehicle_land', 'vehicle_water', 'vehicle_air'],
  /** EquipmentSlot::LIST slot_limit (check_slot_limit) — 1 unless listed: ring 2, waist 3. */
  slotLimits: { '3AD8CEE8-D487-68B1-33F0-CFE04336F9B8': 2, 'B52059C5-526B-3F8C-E906-0D40FA23A593': 3 } as Record<string, number>,
  /** EquipmentSlot 'waist' — the one slot with a rule beyond its count (waistBeltVerdict). */
  waist: 'B52059C5-526B-3F8C-E906-0D40FA23A593',
  /** FT_WAIST_BELT_ITEM_IDS (functions.php) — the catalog items that ARE belts (explicit, never inferred; owner 2026-10-08; round 3 adds
   *  Band of Hammerstone + every sash, skirts stay off). Keep in step:
   *  tools/test/waist-belt-php-ts-parity.mjs fails when the two lists differ. */
  waistBelts: [
    '5A7EF714-3C3C-45A2-9FB4-678AC0C69D3F', // Belt, Leather
    'A903121E-ED84-BBFD-2B28-92A9252CC909', // Belt, Rope
    '5262652D-1547-5A4D-D07A-E5F856358F9B', // Belt of Dwarvenkind
    'DE748F2E-6717-1798-EC26-40F4717E457C', // Belt of Cloud Giant Strength
    '060E648E-B043-E94F-A5C0-D106609047E0', // Belt of Fire Giant Strength
    '16243059-D1ED-69FB-534A-4AA5FDCEC9FB', // Belt of Frost Giant Strength
    '566A65F9-195D-8B2B-0230-F5EA7AD4C490', // Belt of Hill Giant Strength
    '154AB6A3-7981-5C0C-094A-3BB95E0B95DF', // Belt of Stone Giant Strength
    '0F2D3C94-6C1F-A7C6-EE31-0C9B1DE8126E', // Belt of Storm Giant Strength
    '7D022924-A910-4488-BE1B-C2F7EC0DCA1F', // Belt of the Arbiter
    '7C0CA10B-72EB-4F1C-9AFC-450C9BDECFC5', // Band of Hammerstone  (owner 2026-10-08, round 3)
    '68908ACC-2579-43C2-928C-1616B6C06855', // Sash, Cloth  (owner 2026-10-08, round 3)
    'F54FB2B4-2184-44ED-9839-535A97E561B0', // Sash, Leather  (owner 2026-10-08, round 3)
    'D79141DD-73D6-4E2A-B6C4-87A9644E7DF3', // Sash, Silk  (owner 2026-10-08, round 3)
  ] as readonly string[],
  /** StatusEffect 'donning' / 'doffing' — the canonical timed statuses a worn-gear start applies (never minted). */
  donning: 'D343892B-338C-4FBE-BEA0-2015FE503F43',
  doffing: '398DD6E8-AFAE-EA0D-6FE9-C22842D53775',
} as const;

/** check_slot_limit() for an equip into `slot`: `equippedInSlot` = the creature's TOP-LEVEL rows already in that slot
 *  (count_equipped_items reads $possessor['inventory'] only). */
export function slotLimitVerdict(slot: string, equippedInSlot: number): RuleVerdict {
  const limit = EQUIP_RULE_CONSTANTS.slotLimits[U(slot)] ?? 1;
  return equippedInSlot + 1 > limit
    ? { ok: false, error: `The equipment slot is full. You already have ${equippedInSlot} item(s) equipped (limit ${limit}).` }
    : { ok: true };
}

/** FT_WAIST_SECOND_BELT_MESSAGE (functions.php) — the words are the twin's. */
export const WAIST_SECOND_BELT_MESSAGE = 'You are already wearing a belt. Pouches, cases and other waist items can go beside it, but a second belt cannot.';

/** ft_waist_item_is_belt() twin — owner rulings 2026-10-08: a belt is one of the EXPLICITLY defined belts (EQUIP_RULE_CONSTANTS.waistBelts),
 *  read by the row's template id (inventory_item_id, else items.item_id); every other waist item may share the Waist with it. */
export function waistItemIsBelt(item: Row, r: Pick<EquipRuleReaders, 'inventoryField'>): boolean {
  const items = item.items && typeof item.items === 'object' && !Array.isArray(item.items) ? item.items as Row : null;
  const id = U(r.inventoryField(item, 'inventory_item_id')) || U(items?.item_id ?? item.item_id);
  return id !== '' && EQUIP_RULE_CONSTANTS.waistBelts.includes(id);
}

/** ft_waist_second_belt_refusal() twin: ONE belt on the Waist (its slot_limit 3 still counts every item — slotLimitVerdict). `worn` = the
 *  (fat) rows already worn top-level in `slot`. Two rings need no rule: the Ring slot_limit is 2. */
export function waistBeltVerdict(slot: string, item: Row, worn: Row[], r: Pick<EquipRuleReaders, 'inventoryField'>): RuleVerdict {
  if (U(slot) !== U(EQUIP_RULE_CONSTANTS.waist) || !waistItemIsBelt(item, r)) return { ok: true };
  return worn.some(w => waistItemIsBelt(w, r)) ? { ok: false, error: WAIST_SECOND_BELT_MESSAGE } : { ok: true };
}

export interface EquipRuleReaders {
  /** layer_value twin on a (thin or fat) row — the page's bridged reader. */
  value(row: Row | null | undefined, key: string): unknown;
  /** layer_entries twin. */
  entries(row: Row | null | undefined, key: string): unknown[];
  /** item_field('item_type') of a row. */
  itemType(row: Row | null | undefined): string;
  /** inventory_field of a row. */
  inventoryField(row: Row | null | undefined, key: string): unknown;
  /** The creature's resolved SizeType id (computed.creature.size.breakdown.final_size_id). */
  sizeId(caster: Row): string;
  /** The creature's Body total. */
  body(caster: Row): number;
  /** A creature's compiled equipped counts (computed.creature.equipped.*). */
  count(caster: Row, key: string): number;
  /** Does the creature HOLD this passive / feat id (directly, or a passive a held feat grants)? null = a feat the page cannot resolve. */
  holds(caster: Row, id: string): boolean | null;
  /** Does ANY source give the creature this layer (find_caster_layer_provider_arr + resolve_adv_dis_layer_sources)? null = undecidable. */
  provides(caster: Row, layer: string): boolean | null;
  /** The item's always-on passives: its own passive_ability entries + the enchantment-granted ones (validate_equip_size_match). */
  itemPassives(row: Row): string[];
}

const U = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const nonEmpty = (v: unknown): boolean => !(v === undefined || v === null || v === false || v === 0 || v === '' || v === '0'
  || (Array.isArray(v) && v.length === 0));

/** validate_equip_size_match() twin. */
export function sizeVerdict(caster: Row, item: Row, r: EquipRuleReaders): RuleVerdict {
  const size = U(r.sizeId(caster));
  const type = r.itemType(item);
  const passives = r.itemPassives(item).map(U);
  const fit = EQUIP_RULE_CONSTANTS.oversized[size];
  if (fit) {
    const need = type === 'weapon' ? fit.weapon : type === 'armor' ? fit.armor : null;
    if (need && !passives.includes(U(need))) return { ok: false, error: `This ${type} does not match your creature size and cannot be equipped.` };
  }
  if ((EQUIP_RULE_CONSTANTS.standard as readonly string[]).includes(size) && passives.length) {
    const all = Object.values(EQUIP_RULE_CONSTANTS.oversized).flatMap(p => [U(p.weapon), U(p.armor)]);
    if (passives.some(p => all.includes(p))) return { ok: false, error: `This ${type === 'weapon' ? 'weapon' : 'armor'} is oversized for your creature size and cannot be equipped.` };
  }
  return { ok: true };
}

/** The heavy-weapon Body rule (both paths). */
export function heavyVerdict(caster: Row, item: Row, r: EquipRuleReaders): RuleVerdict {
  return nonEmpty(r.value(item, 'weapon_heavy')) && r.body(caster) < 2 ? { ok: false, error: 'This weapon is too heavy for you to equip.' } : { ok: true };
}

/**
 * validate_equip_conditions() twin for the FREE-HAND equip into `slot` (the swap runs only size + heavy — equipSwapVerdict). `occupants`
 * are the rows in the hands now (main / off) for the two-weapon light rule.
 */
export function equipFreeHandVerdict(caster: Row, item: Row, slot: string, slots: { main_hand: string; off_hand: string; both_hands: string },
  occupants: { main: Row | null; off: Row | null }, r: EquipRuleReaders): RuleVerdict {
  const type = r.itemType(item);
  const qty = Number(r.inventoryField(item, 'item_qty') ?? 1);
  if (qty > 1) return { ok: false, error: 'You cannot equip a stack of more than one item into an equipment slot.' };
  if ((EQUIP_RULE_CONSTANTS.vehicles as readonly string[]).includes(type)) return { ok: false, error: 'Vehicles cannot be equipped.' };
  // N6c (worker pick, owner to review): legacy soft-downgraded this to an "unequipped move" of the row onto the holder it already has (a no-op
  // that still charged the equip's AP) — the browser refuses it with the legacy words instead; nothing moves, nothing is spent.
  if (nonEmpty(r.inventoryField(item, 'inventory_unidentified'))) return { ok: false, error: 'Unidentified items cannot be equipped.' };
  const both = U(slot) === U(slots.both_hands), hand = U(slot) === U(slots.main_hand) || U(slot) === U(slots.off_hand);
  if (both && (r.count(caster, 'count_main_hand_equipped') > 0 || r.count(caster, 'count_off_hand_equipped') > 0)) {
    return { ok: false, error: 'You cannot equip a two-handed item while you have a main hand or off hand item equipped.' };
  }
  if (hand && r.count(caster, 'count_both_hands_equipped') > 0) {
    return { ok: false, error: 'You cannot equip an item into your main or off hand while you have a two-handed item equipped.' };
  }
  const heavy = heavyVerdict(caster, item, r);
  if (!('ok' in heavy) || !heavy.ok) return heavy;
  const wc = U(r.value(item, 'weapon_classification'));
  if (wc && (type === 'weapon' || type === 'natural_weapon')) {
    const req = EQUIP_RULE_CONSTANTS.colossal[wc];
    if (req) {
      const passive = r.holds(caster, req.passive), feat = r.holds(caster, req.feat);
      if (passive === null || feat === null) return { unknown: 'colossal-feat' };
      if (!passive && !feat) {
        const melee = wc === 'B66B3311-4A62-5531-D446-CE4A565FD181';
        return { ok: false, error: `You need the Colossal ${melee ? 'Melee' : 'Ranged'} Weapon Master feat or the Colossal ${melee ? 'Melee' : 'Ranged'} Weapon Training passive ability to equip this Colossal ${melee ? 'Melee' : 'Ranged'} weapon.` };
      }
    }
  }
  const size = sizeVerdict(caster, item, r);
  if (!('ok' in size) || !size.ok) return size;
  // validate_two_weapon_fighting() — its four refusals, verbatim, once the creature already holds a hand weapon.
  if (hand && nonEmpty(r.value(item, 'weapon_classification'))) {
    const mainWeapon = r.count(caster, 'count_main_hand_weapon_equipped'), offWeapon = r.count(caster, 'count_off_hand_weapon_equipped');
    if (mainWeapon + offWeapon > 0) {
      const light = (row: Row | null): boolean => !!row && nonEmpty(r.value(row, 'weapon_light'));
      const newLight = nonEmpty(r.value(item, 'weapon_light')), mainLight = light(occupants.main), offLight = light(occupants.off);
      const toMain = U(slot) === U(slots.main_hand), toOff = U(slot) === U(slots.off_hand);
      // WeaponGripFeat::possessorHasGrip: the feat held, or the passive it grants held (directly or through any feat).
      const grip = (g: { feat: string; passive: string }): boolean | null => {
        const f = r.holds(caster, g.feat), p = r.holds(caster, g.passive);
        return f === true || p === true ? true : f === null || p === null ? null : false;
      };
      const titans = grip(EQUIP_RULE_CONSTANTS.grip.titans_grip), shirion = grip(EQUIP_RULE_CONSTANTS.grip.secrets_of_shirion);
      const refusal = (needs: (boolean | null)[], message: string): RuleVerdict | null => {
        if (needs.some(v => v === true)) return null;       // a grip that lifts the rule is held
        if (needs.some(v => v === null)) return { unknown: 'grip-feat' };
        return { ok: false, error: message };
      };
      const checks: [boolean, (boolean | null)[], string][] = [
        [toMain && !newLight && offWeapon > 0 && !offLight, [titans],
          "You can only equip a light weapon into your main hand when you already have a non-light weapon equipped in your off hand without the Titan's Grip feat."],
        [toOff && !newLight && mainWeapon > 0 && !mainLight, [titans],
          "You can only equip a light weapon into your off hand when you already have a non-light weapon equipped in your main hand without the Titan's Grip feat."],
        [toMain && newLight && !offLight, [shirion, titans],
          "You cannot equip a light weapon into your main hand while you have a non-light, one-handed weapon equipped in your off hand without the Secrets of Shirion or Titan's Grip feat."],
        [toOff && newLight && !mainLight, [shirion, titans],
          "You cannot equip a light weapon into your off hand while you have a non-light, one-handed weapon equipped in your main hand without the Secrets of Shirion or Titan's Grip feat."],
      ];
      for (const [applies, needs, message] of checks) {
        if (!applies) continue;
        const v = refusal(needs, message);
        if (v) return v;
      }
    }
  }
  return { ok: true };
}

/** The SWAP's count-independent eligibility (equip_or_swap_inventory_inc.php): size + heavy only. */
export function equipSwapVerdict(caster: Row, item: Row, r: EquipRuleReaders): RuleVerdict {
  const size = sizeVerdict(caster, item, r);
  if (!('ok' in size) || !size.ok) return size;
  return heavyVerdict(caster, item, r);
}

/** One leg of check_and_update_caster_aps_for_move_inventory() in combat: 'equip' (equip_inventory) or 'stow' (move_inventory). The PHP
 *  order: the base (1; a shield 2; else a start-don / start-doff 3), THEN the Fast Draw override for a weapon equip (review S2b rv3 #4). */
export function apLegCost(caster: Row, row: Row, leg: 'equip' | 'stow', r: EquipRuleReaders, ignoreDonDoff = false): number | null {
  const type = r.itemType(row);
  const shield = type === 'armor' && U(r.value(row, 'armor_type')) === U(EQUIP_RULE_CONSTANTS.shield);
  let cost = 1;
  if (shield) cost = 2;
  // `ignoreDonDoff` = PHP's $ignore_donning_doffing (N6b: a campsite / stronghold / no-campaign move skips the timer — no 3-AP start).
  else if (!ignoreDonDoff && leg === 'equip' && nonEmpty(r.value(row, 'donning_time_int'))) cost = 3;
  else if (!ignoreDonDoff && leg === 'stow' && nonEmpty(r.value(row, 'doffing_time_int')) && nonEmpty(r.inventoryField(row, 'equipped'))) cost = 3;
  if (leg === 'equip' && type === 'weapon') {
    const fast = r.provides(caster, 'fast_draw');
    if (fast === null) return null;
    if (fast) cost = 0;
  }
  return cost;
}
